import { Link } from "react-router-dom"
import { useState, useRef, useEffect } from "react"
import { Search, MapPin, ChevronDown, Phone, Calendar, ChevronLeft, ChevronRight } from "lucide-react"
import HealthArticlesWidget from "./widgets/healtharticles"
import Testimonialswidget from "./widgets/testimonials"

export function MaternityCareLayout({
  title,
  heroTagline,
  heroImage,
  highlights = [],
  sectionTitle,
  description,
  readMoreContent,
  contentImage,
  doctors = [],
  faqs = [],
  mediaItems = [],
}) {
  const [openFaq, setOpenFaq] = useState(null)
  const [showMore, setShowMore] = useState(false)
  const scrollRef = useRef(null)

  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
        if (scrollLeft + clientWidth >= scrollWidth) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' })
        } else {
          scrollRef.current.scrollBy({ left: 280, behavior: 'smooth' })
        }
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 280
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <main>
        {/* HERO with Overlay Card */}
        <section className="relative h-[400px] md:h-[500px] w-full overflow-hidden">
          <img src={heroImage} alt={sectionTitle} className="w-full h-full object-cover brightness-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-pink-900/60 to-purple-900/40" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 w-full px-4">
            <div className="container mx-auto max-w-4xl text-center">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
                <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4">{heroTagline}</h1>
                <div className="flex flex-wrap justify-center gap-4 mt-6">
                  {highlights.slice(0, 3).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full">
                      <item.icon className="w-5 h-5 text-pink-600" />
                      <span className="text-sm font-medium text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTENT with Side Image */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative h-[400px] rounded-3xl overflow-hidden shadow-xl">
                  <img src={contentImage} alt={sectionTitle} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                    <p className="text-white font-semibold text-lg">{sectionTitle}</p>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <span className="inline-block px-4 py-1 bg-pink-100 text-pink-600 rounded-full text-sm font-semibold mb-4">
                  {title}
                </span>
                <h2 className="mb-6 text-3xl md:text-4xl font-bold text-gray-800">{sectionTitle}</h2>
                <p className="text-gray-600 leading-relaxed text-base">{description}</p>
                {readMoreContent && (
                  <>
                    {showMore && <p className="mt-4 text-gray-600 text-base leading-relaxed">{readMoreContent}</p>}
                    <button
                      onClick={() => setShowMore(!showMore)}
                      className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-full font-semibold text-sm hover:bg-pink-700 transition"
                    >
                      {showMore ? "Show Less" : "Read More"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Voices of Trust - Videos Section */}
        <section className="py-16 bg-gradient-to-r from-pink-100 to-purple-100">
          <div className="container px-4 mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-4">Voices of Trust from Our Patients</h2>
                <p className="text-gray-700 text-lg leading-relaxed">Real stories from our patients about their journey to healing with our care.</p>
              </div>

              <div className="relative">
                <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition shadow-lg">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition shadow-lg">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div ref={scrollRef} className="flex gap-4 overflow-x-hidden scroll-smooth">
                  {[
                    { thumb: "/images/portrait-newborn-baby-with-mother.jpg", title: "Patient Testimonial 1" },
                    { thumb: "/images/SecondTrimester.png", title: "Happy Patient 2" },
                    { thumb: "/images/portrait-newborn-baby-with-mother.jpg", title: "Patient Story 3" },
                    { thumb: "/images/SecondTrimester.png", title: "Recovery Journey 4" },
                  ].map((video, i) => (
                    <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg flex-shrink-0 w-64 cursor-pointer group">
                      <img src={video.thumb} alt={video.title} className="w-full h-80 object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition">
                          <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-purple-600 border-b-8 border-b-transparent ml-1"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Centers of Excellence */}
        <section className="py-16 bg-gradient-to-b from-purple-50 to-white">
          <div className="container px-4 mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-4">Centers of Excellence</h2>
              <p className="text-gray-600 max-w-4xl mx-auto">Discover world-class healthcare at our specialized centres of medical innovation. Our advanced facilities offer unmatched expertise in key specialties and super specialties, setting new standards in clinical excellence and patient care.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: "🩺", title: "Prenatal Care", desc: "Comprehensive monitoring and support throughout pregnancy journey." },
                { icon: "👶", title: "Postnatal Care", desc: "Complete recovery support for mother and newborn care guidance." },
                { icon: "🤱", title: "Lactation Support", desc: "Expert breastfeeding assistance and nutritional counseling." },
                { icon: "💉", title: "Vaccination Programs", desc: "Complete immunization schedules for mother and child health." },
                { icon: "🧘", title: "Prenatal Yoga", desc: "Specialized exercises for healthy pregnancy and delivery preparation." },
                { icon: "🩹", title: "High-Risk Pregnancy", desc: "Advanced care for complex pregnancy conditions and monitoring." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition border border-purple-100">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center text-3xl mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-purple-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DOCTORS - Card Grid */}
        <section className="py-16 bg-gradient-to-b from-pink-50 to-white">
          <div className="container px-4 mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Meet Our Specialists</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Expert care from experienced professionals dedicated to your health</p>
            </div>

            <div className="flex items-center max-w-lg mx-auto mb-12 border-2 border-pink-200 rounded-full bg-white overflow-hidden shadow-md">
              <input type="text" placeholder="Search by name or specialty..." className="flex-1 px-6 py-4 text-sm bg-transparent outline-none" />
              <button className="w-12 h-12 bg-pink-600 flex items-center justify-center text-white rounded-full mr-1 hover:bg-pink-700 transition">
                <Search className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {doctors.map((doc, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition group">
                  <div className="h-48 overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100">
                    <img src={doc.image} alt={doc.name} className="w-full h-full object-cover group-hover:scale-105 transition" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{doc.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{doc.designation}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                      <MapPin className="w-4 h-4" />
                      <span>{doc.location}</span>
                    </div>
                    <div className="flex gap-3">
                      <Link to="/book-appointment" className="flex-1 px-4 py-2 text-center text-sm border-2 border-pink-600 text-pink-600 rounded-lg font-semibold hover:bg-pink-50 transition">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Book
                      </Link>
                      <Link to="/book-appointment" className="flex-1 px-4 py-2 text-center text-sm bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 transition">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Call
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <Testimonialswidget />

        {/* Health Articles */}
        <HealthArticlesWidget />

        {/* FAQ - Accordion Style */}
        <section className="py-16 bg-gradient-to-b from-white to-pink-50">
          <div className="container px-4 mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600">Find answers to common questions about our services</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="border-2 border-pink-100 rounded-2xl bg-white overflow-hidden shadow-sm hover:shadow-md transition">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex justify-between items-center w-full p-6 text-left"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq.q}</span>
                    <ChevronDown className={`w-6 h-6 text-pink-600 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-pink-100 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
